import { useWallet } from "@solana/wallet-adapter-react";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { WithdrawalTicket } from "../../helpers/dataStructures";
import { useWithdrawalTickets } from "../../hooks/useWithdrawalTickets";

interface StakeWithdrawalTicketsContextValue {
  tickets: WithdrawalTicket[];
  count: number;
  isLoading: boolean;
  refresh: () => void;
}

const StakeWithdrawalTicketsContext = createContext<StakeWithdrawalTicketsContextValue | null>(
  null
);

export const StakeWithdrawalTicketsProvider = ({
  vaultAddress,
  children,
}: {
  vaultAddress: string;
  children: ReactNode;
}) => {
  const { publicKey } = useWallet();
  const { withdrawalTickets, isLoading, fetchWithdrawalTickets } = useWithdrawalTickets();

  const refresh = useCallback(() => {
    void fetchWithdrawalTickets(vaultAddress, publicKey ?? null);
  }, [vaultAddress, publicKey, fetchWithdrawalTickets]);

  useEffect(() => {
    void fetchWithdrawalTickets(vaultAddress, publicKey ?? null);
  }, [vaultAddress, publicKey, fetchWithdrawalTickets]);

  const value = useMemo(
    () => ({
      tickets: withdrawalTickets,
      count: withdrawalTickets.length,
      isLoading,
      refresh,
    }),
    [withdrawalTickets, isLoading, refresh]
  );

  return (
    <StakeWithdrawalTicketsContext.Provider value={value}>
      {children}
    </StakeWithdrawalTicketsContext.Provider>
  );
};

export const useStakeWithdrawalTickets = (): StakeWithdrawalTicketsContextValue => {
  const ctx = useContext(StakeWithdrawalTicketsContext);
  if (!ctx) {
    throw new Error("useStakeWithdrawalTickets must be used within StakeWithdrawalTicketsProvider");
  }
  return ctx;
};

export const useStakeWithdrawalTicketsState = () => {
  const ctx = useContext(StakeWithdrawalTicketsContext);
  return {
    count: ctx?.count ?? 0,
    isLoading: ctx?.isLoading ?? false,
  };
};

/** Optional hook for components that may render outside the provider. */
export const useStakeWithdrawalTicketCount = (): number => {
  const { count, isLoading } = useStakeWithdrawalTicketsState();
  return isLoading ? 0 : count;
};
